import { Box, Typography, Button, Stack, Divider, Chip } from '@mui/material';
import { alpha } from '@mui/material/styles';
import CheckIcon from '@mui/icons-material/Check';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LanguageIcon from '@mui/icons-material/Language';
import LaptopIcon from '@mui/icons-material/Laptop';
import NotificationsIcon from '@mui/icons-material/Notifications';
import EventNoteOutlinedIcon from '@mui/icons-material/EventNoteOutlined';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import StorefrontIcon from '@mui/icons-material/Storefront';
import { useNavigate } from 'react-router-dom';
import { montoDeReserva } from './montoReserva';
import { dinero } from '../../utils/dinero';

// Al reservar no se cobra nada: el proveedor tiene que aceptar la solicitud. Con tarjeta (o wallet, que el servidor guarda como
// tarjeta) el cargo se hace al aceptar; con SPEI u OXXO la referencia se genera entonces y se ve en «Datos de pago» de la reserva.
const PAGOS = {
  card: {
    nombre: 'Tarjeta de crédito o débito', icono: CreditCardIcon, titulo: 'Se cobra hasta que acepte',
    texto: 'El cargo a tu tarjeta se hace cuando el proveedor acepte. Si la rechaza o no responde a tiempo, no se te cobra nada.',
  },
  spei: {
    nombre: 'Transferencia SPEI', icono: AccountBalanceIcon, titulo: 'Pagas hasta que acepte',
    texto: 'Cuando el proveedor acepte, te avisaremos y verás en tu reserva, en «Datos de pago», la CLABE y la referencia para transferir. Tu PIN aparece cuando se confirme tu pago. Si la rechaza o no responde a tiempo, no pagas nada.',
  },
  oxxo: {
    nombre: 'Efectivo en OXXO', icono: StorefrontIcon, titulo: 'Pagas hasta que acepte',
    texto: 'Cuando el proveedor acepte, te avisaremos y verás en tu reserva, en «Datos de pago», la ficha para pagar en efectivo en OXXO. Tu PIN aparece cuando se confirme tu pago. Si la rechaza o no responde a tiempo, no pagas nada.',
  },
};

const DetailItem = ({ icon: Icon, label, value }) => (
  <Box sx={{
    display: 'flex', alignItems: 'center', gap: 1.5,
    p: 1.5, borderRadius: '12px', bgcolor: (t) => alpha(t.palette.secondary.main, 0.05),
    border: 1, borderColor: (t) => alpha(t.palette.secondary.main, 0.12)
  }}>
    <Box sx={{ color: 'primary.main', display: 'flex', alignItems: 'center' }}>
      <Icon sx={{ fontSize: 20 }} />
    </Box>
    <Box>
      <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
      <Typography variant="body2" fontWeight="bold" color="primary.main">{value}</Typography>
    </Box>
  </Box>
);

const Paso = ({ icon: Icon, titulo, children }) => (
  <Box sx={{ display: 'flex', gap: 1.5 }}>
    <Box sx={{
      width: 36, height: 36, borderRadius: '10px', bgcolor: (t) => alpha(t.palette.secondary.main, 0.1),
      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
    }}>
      <Icon sx={{ fontSize: 18, color: 'primary.main' }} />
    </Box>
    <Box>
      <Typography variant="body2" fontWeight="bold" color="primary.main">{titulo}</Typography>
      <Typography variant="caption" color="text.secondary">{children}</Typography>
    </Box>
  </Box>
);

// metodoPago: con qué quedó la reserva ('card' | 'spei' | 'oxxo'), o null si no lleva pago en línea. promo: el código aplicado o null.
const BookingConfirmation = ({
  booking, service, ownerUser,
  selectedDate, selectedTime,
  selectedLanguage, selectedModality,
  metodoPago, promo
}) => {
  const navigate = useNavigate();

  const bookingRef = booking?._id
    ? `BK-${new Date().getFullYear()}-${booking._id.slice(-5).toUpperCase()}`
    : 'BK-PEND';

  const formatDateLong = (date) => {
    if (!date) return '';
    const days = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
    const months = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
      'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
    const d = new Date(date);
    return `${days[d.getDay()]}, ${d.getDate()} de ${months[d.getMonth()]} de ${d.getFullYear()}`;
  };

  // Lo que se cobrará al aceptar (precio por horas o días, cotización y código, como en el servidor)
  const { precio, unidades, subtotal, descuento, total } = montoDeReserva(booking, promo);
  const unidad = booking?.tipoCosto === 'Día' ? (unidades === 1 ? 'día' : 'días') : 'h';
  const pago = total > 0 ? PAGOS[metodoPago] : null;
  const IconoPago = pago?.icono;
  const authorName = ownerUser ? `${ownerUser.name} ${ownerUser.apellido}` : 'Proveedor';

  return (
    <Box sx={{ maxWidth: 560, mx: 'auto', mt: 12, mb: 8, px: 2 }}>
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Box sx={{
          width: 80, height: 80, borderRadius: '50%',
          bgcolor: 'success.main', display: 'inline-flex',
          alignItems: 'center', justifyContent: 'center', mb: 2
        }}>
          <CheckIcon sx={{ color: 'white', fontSize: 42 }} />
        </Box>
        <Typography variant="h4" fontWeight="900" color="primary.main" sx={{ lineHeight: 1.2 }}>
          Tu solicitud fue enviada
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          El proveedor tiene que aceptarla para que quede agendada.
        </Typography>
      </Box>

      <Box sx={{ bgcolor: 'white', borderRadius: '24px', border: 1, borderColor: 'sutil', p: 3, mb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box sx={{ flex: 1, pr: 1 }}>
            <Typography variant="subtitle1" fontWeight="900" color="primary.main" sx={{ lineHeight: 1.3 }}>
              {service?.nombre}
            </Typography>
            <Typography variant="body2" color="text.secondary">Por {authorName}</Typography>
          </Box>
          <Chip
            label={bookingRef}
            size="small"
            sx={{ bgcolor: 'primary.main', color: 'white', fontWeight: 600, fontSize: '0.62rem', flexShrink: 0 }}
          />
        </Box>

        <Divider sx={{ mb: 2, borderColor: 'sutil' }} />

        <Typography variant="subtitle2" fontWeight="bold" color="primary.main" sx={{ mb: 1.5 }}>
          Detalles de la cita
        </Typography>

        {/* Dos columnas solo si caben: cada cajita necesita hasta 132 px con los textos más largos («Miércoles, 30 de septiembre»,
            «Presencial», «Portugués»); con menos, una columna. El 12px es el gap. */}
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(max(132px, calc((100% - 12px) / 2)), 1fr))', gap: 1.5, mb: 2.5 }}>
          <DetailItem icon={CalendarTodayIcon} label="Fecha" value={formatDateLong(selectedDate)} />
          <DetailItem icon={AccessTimeIcon} label="Hora" value={selectedTime ? `${selectedTime} hrs` : '-'} />
          <DetailItem icon={LaptopIcon} label="Modalidad" value={selectedModality} />
          <DetailItem icon={LanguageIcon} label="Idioma" value={selectedLanguage} />
        </Box>

        <Box sx={{ bgcolor: (t) => alpha(t.palette.secondary.main, 0.05), borderRadius: '12px', p: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, mb: 1 }}>
            <Box>
              <Typography variant="body2" color="text.secondary">Subtotal</Typography>
              {unidades !== 1 && (
                <Typography variant="caption" color="text.secondary" display="block">
                  {dinero(precio)} × {unidades}{' '}{unidad}
                </Typography>
              )}
            </Box>
            <Typography variant="body2" color="primary.main" fontWeight={600}>{dinero(subtotal)}</Typography>
          </Box>
          {descuento > 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, mb: 1 }}>
              <Typography variant="body2" color="success.oscuro">Descuento ({promo.codigo})</Typography>
              <Typography variant="body2" color="success.oscuro" fontWeight={600}>-{dinero(descuento)}</Typography>
            </Box>
          )}
          <Divider sx={{ my: 1, borderColor: (t) => alpha(t.palette.secondary.main, 0.12) }} />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
            {pago ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
                <IconoPago sx={{ fontSize: 14, color: 'text.secondary', flexShrink: 0 }} />
                <Typography variant="caption" color="text.secondary">{pago.nombre}</Typography>
              </Box>
            ) : <Box />}
            <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
              <Typography variant="caption" color="text.secondary" display="block">{pago ? 'Total a pagar' : 'Total'}</Typography>
              <Typography variant="subtitle1" fontWeight="900" color="primary.main">{dinero(total)}</Typography>
            </Box>
          </Box>
        </Box>
      </Box>

      <Button
        variant="outlined"
        fullWidth
        startIcon={<EventNoteOutlinedIcon />}
        onClick={() => navigate('/user/reservas-proveedor')}
        sx={{
          mb: 3, py: 1.5, borderRadius: '14px', textTransform: 'none',
          fontWeight: 700, borderColor: 'divider', color: 'primary.main',
          '&:hover': { borderColor: 'primary.main', bgcolor: (t) => alpha(t.palette.secondary.main, 0.05) }
        }}
      >
        Ver mis reservas
      </Button>

      <Box sx={{ bgcolor: 'white', borderRadius: '24px', border: 1, borderColor: 'sutil', p: 3, mb: 3 }}>
        <Typography variant="subtitle2" fontWeight="bold" color="primary.main" sx={{ mb: 2 }}>
          ¿Qué sigue ahora?
        </Typography>
        <Stack spacing={2}>
          <Paso icon={NotificationsIcon} titulo="Te avisamos cuando responda">
            Cuando el proveedor acepte o rechace tu solicitud, te llegará una notificación y un correo.
          </Paso>
          {pago && <Paso icon={pago.icono} titulo={pago.titulo}>{pago.texto}</Paso>}
        </Stack>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
        <Button
          variant="outlined"
          onClick={() => navigate('/buscar')}
          sx={{
            py: 1.5, borderRadius: '14px', textTransform: 'none', fontWeight: 700,
            borderColor: 'divider', color: 'primary.main',
            '&:hover': { borderColor: 'primary.main', bgcolor: (t) => alpha(t.palette.secondary.main, 0.05) }
          }}
        >
          Explorar más servicios
        </Button>
        <Button
          variant="contained"
          onClick={() => navigate('/')}
          sx={{
            py: 1.5, borderRadius: '14px', textTransform: 'none', fontWeight: 700,
            bgcolor: 'primary.main', color: 'white',
            boxShadow: (t) => `0px 8px 16px ${alpha(t.palette.primary.main, 0.2)}`,
            '&:hover': { bgcolor: 'primary.dark' }
          }}
        >
          Ir al inicio
        </Button>
      </Box>
    </Box>
  );
};

export default BookingConfirmation;
